import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Col, Input, Row, Spin } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import Truncate from 'components/Truncate';
import { formatDate } from 'helpers/date';
import { parse } from 'query-string';
import PermissionHide from 'v2/components/Shared/PermissionHide';

import Chat from './components/Chat';
import New from './components/New';
import { useMessageChats } from './useMessageChats';

interface IProps {
  match: any;
  location: any;
  history: any;
}

const Messages: React.FC<IProps> = ({ match, location }) => {
  const [search, setSearch] = useState('');
  const [chatId, setChatId] = useState(0);
  const [modalNew, setModalNew] = useState(false);

  const { chats, isLoading } = useMessageChats();

  useEffect(() => {
    const lastChatId = chats[0]?.id || 0;

    if (lastChatId !== chatId && chatId === 0) {
      setChatId(lastChatId);
    }
  }, [chats, chatId, setChatId]);

  const handleSearch = useCallback((event: any) => {
    const value = event.target.value;
    setSearch(value);

    const filter = value.toLowerCase();
    const elements: any = document.getElementsByClassName('--search-chat');

    for (let i = 0; i < elements.length; i++) {
      elements[i].style.display = elements[i].innerText.toLowerCase().includes(filter) ? 'flex' : 'none';
    }
  }, []);

  const resetSearch = useCallback(() => {
    setSearch('');
    const elements: any = document.getElementsByClassName('--search-chat');

    for (let i = 0; i < elements.length; i++) {
      elements[i].style.display = 'flex';
    }
  }, []);

  const handleSelectMessage = useCallback(
    async (id: number | undefined) => {
      if (!id) {
        return;
      }

      if (id !== chatId) {
        setChatId(id);
        resetSearch();
      }
    },
    [chatId, resetSearch, setChatId]
  );

  const toggleModalNew = useCallback(() => {
    setModalNew(prev => !prev);
  }, []);

  const urlInitializedRef = useRef<string | null>(null);

  useEffect(() => {
    const params = match.params;
    const queryParams = parse(location.search);
    const affiliateId = queryParams.affiliate ? parseInt(queryParams.affiliate as string) : null;

    const urlKey = `${params.id ?? ''}-${affiliateId ?? ''}`;
    if (urlInitializedRef.current === urlKey) return;

    try {
      const accepts = chats.map((chat: any) => chat.id);

      if (params.id) {
        const parsedId = parseInt(params.id);

        if (accepts.includes(parsedId)) {
          setChatId(parsedId);
          resetSearch();
          urlInitializedRef.current = urlKey;
        }

        return;
      }

      if (affiliateId) {
        const selectedChat = chats.find((chat: any) => chat.outer.id === affiliateId);

        if (selectedChat?.id) {
          setChatId(selectedChat.id);
          resetSearch();
          urlInitializedRef.current = urlKey;
        }
      }
    } catch (error) {
      toast.error('Ocorreu um erro ao tentar carregar mensagens');
    }
  }, [chats, location.search, match.params, resetSearch]);

  return (
    <div className='animate-fadeIn w-full'>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <div className='bg-surface-default size-full rounded-sm shadow-sm'>
            <div className='border-outline-default flex items-center justify-between border-b px-4 py-3 text-base font-medium'>
              <div>Conversas</div>
              <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
                <Button type='primary' onClick={toggleModalNew} icon={<Icon.Plus  />}>
                  Nova
                </Button>
              </PermissionHide>
            </div>

            <div className='px-4 py-3'>
              <Input
                prefix={<Icon.Search className='text-content-subtitle'  />}
                placeholder='Buscar conversa...'
                value={search}
                onChange={handleSearch}
                disabled={isLoading}
                allowClear
              />
            </div>

            <div className='max-h-[65vh] min-h-[65vh] w-full overflow-auto'>
              {isLoading && (
                <div className='flex size-full items-center justify-center'>
                  <Spin />
                </div>
              )}

              {!isLoading && chats && chats.length === 0 && (
                <div className='text-content-subtitle w-full py-6 text-center text-sm'>
                  Nenhuma conversa encontrada.
                </div>
              )}

              {!isLoading &&
                chats &&
                chats.length > 0 &&
                chats.map((chat: any, index: any) => {
                  return (
                    <div
                      key={'chat-' + index}
                      className={`hover:bg-fill-secondary text-content-body flex cursor-pointer items-center px-4 py-2 text-sm opacity-80 ${
                        chat.not_read_count > 0 ? 'opacity-100' : ''
                      } --search-chat ${chatId == chat.id ? 'bg-fill-secondary opacity-100' : ''}`}
                      onClick={() => handleSelectMessage(chat.id)}
                    >
                      <div
                        className={`bg-fill-secondary border-outline-default relative mr-3 size-10.5 overflow-hidden rounded-full border ${
                          chat.not_read_count > 0 ? '[&_span]:bg-(--uizz-antd-color-success)' : ''
                        }`}
                      >
                        <img
                          src={chat.outer.avatar ? chat.outer.avatar : '/img/avatar-default.png'}
                          alt=''
                          className='absolute inset-0 size-full object-cover'
                        />
                        <span className='bg-fill absolute -top-0.5 left-0 z-2 size-3 rounded-full'></span>
                        {chat.outer.qualification ? (
                          <img
                            src={`https://cdn.eduzzcdn.com/badges/${chat.outer.qualification.badge_elite}${
                              chat.outer.qualification.has_plus ? '-plus' : ''
                            }.svg`}
                            alt=''
                            className='bg-container absolute -right-1 -bottom-1 z-2 size-5 rounded-full object-cover'
                          />
                        ) : null}
                      </div>
                      <div>
                        <div className='mb-1 block font-medium'>
                          <Truncate count={25} final='...'>
                            {chat.outer.name}
                          </Truncate>
                        </div>
                        <div className='text-content-subtitle truncate text-sm'>
                          <Truncate count={28} final='...'>
                            {chat.last.content}
                          </Truncate>
                        </div>
                        <div className='text-content-subtitle text-xs'>
                          {chat.last.created_at && formatDate(chat.last.created_at, 'dd/MM/yyyy H:mm')}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </Col>

        <Col xs={24} md={16}>
          <Chat chatId={chatId} />
        </Col>
      </Row>

      {modalNew && <New active={modalNew} closeModal={toggleModalNew} />}
    </div>
  );
};

export default Messages;
